import type { Icon as PhosphorIcon } from '@phosphor-icons/react';
import {
    Fingerprint,
    GoogleLogo,
    Key,
    ShieldCheck,
    UserCircle,
    WindowsLogo,
} from '@phosphor-icons/react';
import { BadgeColor, BadgeSize, ExBadge } from '@boomi/exosphere';
import type { IdpType, IdentityProvider } from './identityProviders';
import styles from './IdentityProviderRow.module.css';

interface IdentityProviderRowProps {
    idp: IdentityProvider;
    onSelect?: (id: string) => void;
}

interface IconConfig {
    icon: PhosphorIcon;
    iconBg: string;
    iconColor: string;
}

const NAME_ICON_MAP: Record<string, IconConfig> = {
    'Google Workspace': { icon: GoogleLogo, iconBg: '#fff', iconColor: '#4285f4' },
    'Microsoft Azure AD': { icon: WindowsLogo, iconBg: '#0078d4', iconColor: '#fff' },
    Okta: { icon: ShieldCheck, iconBg: '#007dc1', iconColor: '#fff' },
    Auth0: { icon: Key, iconBg: '#eb5424', iconColor: '#fff' },
    OneLogin: { icon: UserCircle, iconBg: '#f3f4f6', iconColor: '#374151' },
    'Ping Identity': { icon: Fingerprint, iconBg: '#c00', iconColor: '#fff' },
};

const TYPE_ICON_FALLBACK: Record<IdpType, IconConfig> = {
    OAUTH: { icon: Key, iconBg: '#f3f4f6', iconColor: '#374151' },
    SAML: { icon: ShieldCheck, iconBg: '#f3f4f6', iconColor: '#374151' },
};

const protocolLabel: Record<IdpType, string> = {
    OAUTH: 'OAuth',
    SAML: 'SAML',
};

const IdentityProviderRow = ({ idp, onSelect }: IdentityProviderRowProps) => {
    const { id, name, type } = idp;
    const { icon: Icon, iconBg, iconColor } = NAME_ICON_MAP[name] ?? TYPE_ICON_FALLBACK[type];

    return (
        <button
            type="button"
            className={styles.row}
            onClick={() => onSelect?.(id)}
            aria-label={`${name} — ${type}`}
        >
            <div className={styles.iconWrap} style={{ background: iconBg }}>
                <Icon size={20} color={iconColor} weight="fill" />
            </div>
            <div className={styles.info}>
                <span className={styles.name}>{name}</span>
                <ExBadge
                    color={type === 'OAUTH' ? BadgeColor.BLUE : BadgeColor.GREEN}
                    size={BadgeSize.EXTRASMALL}
                >
                    {protocolLabel[type]}
                </ExBadge>
            </div>
        </button>
    );
};

export default IdentityProviderRow;
